
import React, { useState, useEffect } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { useToast } from '@/hooks/use-toast';
import { FilesList } from './FilesList';

interface FileImport {
  id: string;
  file_name: string;
  file_type: string;
  file_size: number;
  storage_path?: string;
  connection_id: string;
  created_at: string;
}

interface OrganisationFilesTabProps {
  organisation: any;
}

const OrganisationFilesTab: React.FC<OrganisationFilesTabProps> = ({ organisation }) => {
  const [files, setFiles] = useState<FileImport[]>([]);
  const [loading, setLoading] = useState(true);
  const { toast } = useToast();

  const fetchFiles = async () => {
    try {
      setLoading(true);

      // Get all connection IDs from the organisation
      const connectionIds: string[] = [];
      if (organisation?.connections) {
        Object.values(organisation.connections).forEach((conn: any) => {
          if (conn.connectorId) {
            connectionIds.push(conn.connectorId);
          }
          if (conn.id && conn.id !== conn.connectorId) {
            connectionIds.push(conn.id);
          }
        });
      }

      if (connectionIds.length === 0) {
        setFiles([]);
        return;
      }

      // Fetch files from file_imports table
      const { data: fileImports, error } = await supabase
        .from('file_imports')
        .select('*')
        .in('connection_id', connectionIds)
        .order('created_at', { ascending: false });

      if (error) {
        console.error('Error fetching files:', error);
        toast({
          title: 'Error',
          description: 'Failed to load files',
          variant: 'destructive',
        });
        return;
      }

      setFiles(fileImports || []);
    } catch (error) {
      console.error('Error in fetchFiles:', error);
      toast({
        title: 'Error',
        description: 'Failed to load files',
        variant: 'destructive',
      });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchFiles();
  }, [organisation]);

  const handleDeleteFile = async (fileId: string) => {
    try {
      const { error } = await supabase
        .from('file_imports')
        .delete()
        .eq('id', fileId);

      if (error) {
        throw error;
      }

      setFiles(files.filter(file => file.id !== fileId));
      toast({
        title: 'Success',
        description: 'File deleted successfully',
      });
    } catch (error) {
      console.error('Error deleting file:', error);
      toast({
        title: 'Error',
        description: 'Failed to delete file',
        variant: 'destructive',
      });
    }
  };

  if (loading) {
    return (
      <div className="bg-white rounded-lg shadow p-6 mt-6">
        <div className="flex items-center justify-center py-12">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
        </div>
      </div>
    );
  }

  return (
    <div data-testid="organisation-files-tab" className="bg-white rounded-lg shadow p-6 mt-6">
      <div className="flex justify-between items-center mb-6">
        <h2 className="text-xl font-semibold text-gray-900">Files</h2>
        <div className="text-sm text-gray-500">
          {files.length} {files.length === 1 ? 'file' : 'files'}
        </div>
      </div>

      <FilesList
        files={files}
        onDeleteFile={handleDeleteFile}
        onRefresh={fetchFiles}
      />
    </div>
  );
};

export default OrganisationFilesTab;
